import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { BackgroundJobPageDto } from "@/api/dtos";
import type { BackgroundJob } from "./_models/BackgroundJob";
import { toBackgroundJobFromBackgroundJobDto } from "./_transformers/toBackgroundJobFromBackgroundJobDto";
import { BackgroundJobTags } from "./BackgroundJobTags";

export interface GetBackgroundJobListArgs {
  kind?: string;
  subject?: string;
  limit?: number;
}

const toParams = (args: GetBackgroundJobListArgs): Record<string, string> => {
  const params: Record<string, string> = {};
  if (args.kind) params.kind = args.kind;
  if (args.subject) params.subject = args.subject;
  if (args.limit !== undefined) params.limit = String(args.limit);
  return params;
};

/**
 * Background jobs (GET /background-jobs), newest first, e.g. a screen's latest run:
 * `{ kind: "importOrderStatuses", subject: "261023", limit: 1 }`. Poll while it's active.
 */
export const getBackgroundJobListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBackgroundJobList: builder.query<BackgroundJob[], GetBackgroundJobListArgs>({
      queryFn: async (args) => {
        try {
          const params = toParams(args);
          const res = await axios.get<BackgroundJobPageDto>(
            `${backendBase()}/background-jobs`,
            { params, headers: { Accept: "application/json" } },
          );
          const jobs = res.data.backgroundJobs.map((x) => toBackgroundJobFromBackgroundJobDto(x));
          return { data: jobs };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: BackgroundJobTags.BackgroundJob, id: "LIST" }],
    }),
  }),
});

export const getBackgroundJobList = getBackgroundJobListApi.endpoints.getBackgroundJobList;
